/**
 * Utility method to create a reducer from a map,
 * with initial state
 * @param initialState
 * @param reducerMap
 * @returns {Function}
 */

import { useEffect } from 'react';

interface Action<P = any> {
    type: string;
    payload: P;
}

interface ReducerMap<S> {
    [key: string]: (state: S, payload: any) => S;
}

export function createReducer<S>(initialState: S, reducerMap: ReducerMap<S>) {
    return (state: S = initialState, action: Action): S => {
        const reducer = reducerMap[action.type];

        return reducer
            ? reducer(state, action.payload)
            : state;
    };
}

/**
 * function to return the username from email address
 * @param email 
 * @returns username
 */
export function decodeEmail(email: string) {
    try {
        return email.replace(/@[^@]+$/, '');
    }
    catch {
        return email;
    }
}

export function extractMultipleValues(value: string) {
    if (value) {
        return value.split('\n').join(',');
    }
    return '';
}

export function extractCommentData(data: string) {
    if (data) {
        const splitedString = data.split("\n");
        const comments = splitedString.map(comment => {
            const splitedComment = comment.split("]");
            return {
                name: splitedComment[0]?.replace("[", ""),
                date: splitedComment[1]?.replace("[", ""),
                text: splitedComment[2]
            }
        });
        return comments;
    }
    return [];
}

export function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function reorderArray(array: any[], oldIndex: any, newIndex: number) {
    if (newIndex >= array.length) {
        newIndex = array.length - 1;
    }
    array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
    return array;
}

export function prependZero(num: string | number) {
    return ('0' + num).slice(-2);
} 

/**
 * A custom hook that adds an event listener to the window object and optionally debounces the callback.
 *
 * @param {string} event - The name of the event to listen for (e.g., 'resize', 'scroll').
 * @param {EventListener} callback - The function to call when the event is triggered.
 * @param {number} [delay=0] - The debounce delay in milliseconds. If 0, the callback is not debounced.
 *
 * @example
 * useWindowEvent('resize', handleResize, 300);
 */
export function useWindowEvent( event : string, callback : EventListener, delay = 0 ){
    useEffect(() => {
        let timeoutId;

        const debouncedCallback = delay ?
            (e) => {
                timeoutId && clearTimeout(timeoutId);

                timeoutId = setTimeout(() => {
                    callback(e);
                }, delay);
            }
        : callback;

        window.addEventListener(event, debouncedCallback);

        return () => {
            delay && clearTimeout(timeoutId); // Clean up the timeout
            window.removeEventListener(event, debouncedCallback);
        };
    }, [ event, callback, delay ]);
}

 export function adjustCurrentPage(currentPage, totalRecords, pageSize) {
    let totalPages = Math.ceil(totalRecords / pageSize);
    return Math.min(currentPage, totalPages) || 1;
}
function parseFlexibleDate(value) {
    if (!value) return null;

    const val = value.toString().trim();

    // Handle date ranges like "09-08-2025 to 09-14-2025"
    if (val.includes(" to ")) {
        const [start] = val.split(" to ");
        return parseFlexibleDate(start); // sort by start date
    }

    // Handle MM-DD-YYYY or similar
    const mmddyyyyMatch = val.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (mmddyyyyMatch) {
        const [_, mm, dd, yyyy] = mmddyyyyMatch;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime();
    }

    // Handle "Oct 2024", "September 2025", etc.
    const monthYearMatch = val.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{4})$/i);
    if (monthYearMatch) {
        const [_, monthStr, year] = monthYearMatch;
        return new Date(`${monthStr} 01, ${year}`).getTime();
    }

    // Fallback to native Date parsing
    const date = new Date(val);
    if (!isNaN(date.getTime())) {
        return date.getTime();
    }

    return null; 
}

export function nonPaginatedSorting(sort, tableData) {
    sort.forEach((item) => {
        const isDescending = item?.direction === "desc";
        const id = item?.id || item?.key;

        if (tableData[0] && id in tableData[0]) {
            tableData.sort((a, b) => {
                const isDate = id.toLowerCase().includes("date") || id.toLowerCase().includes("period");
                let aValue = a[id];
                let bValue = b[id];

                const aIsNull = aValue === null || aValue === undefined;
                const bIsNull = bValue === null || bValue === undefined;

                if (aIsNull && bIsNull) return 0;
                // If one is null, decide where to place it (usually nulls at bottom)
                if (aIsNull) return isDescending ? -1 : 1;
                if (bIsNull) return isDescending ? 1 : -1;

                // Now handle data types safely
                if (isDate) {
                    const dateA = parseFlexibleDate(aValue);
                    const dateB = parseFlexibleDate(bValue);

                    if (!dateA && !dateB) return 0;
                    if (!dateA) return isDescending ? -1 : 1;
                    if (!dateB) return isDescending ? 1 : -1;

                    return isDescending ? dateB - dateA : dateA - dateB;
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    const aNum = Number(aValue);
                    const bNum = Number(bValue);
                    if (!isNaN(aNum) && !isNaN(bNum) && aValue.trim() !== '' && bValue.trim() !== '') {
                        return isDescending ? bNum - aNum : aNum - bNum;
                    }
                    const comparison = aValue.localeCompare(bValue);
                    return isDescending ? -comparison : comparison;
                }
                if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
                    if (aValue === bValue) return 0;
                    return isDescending ? (bValue ? 1 : -1) : (aValue ? 1 : -1);
                }

                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return isDescending ? bValue - aValue : aValue - bValue;
                }

                const aStr = String(aValue);
                const bStr = String(bValue);
                const comparison = aStr.localeCompare(bStr);
                return isDescending ? -comparison : comparison;
            });
        }
    });

    return { sort, tableData };
}

const parseNumber = (val) => (val !== "" ? parseInt(val, 10) : null);

export function handleRule(rule, covered, excluded, maxRef) {
    const { equator: eq, value: rawValue, startsAt: rawStarts, endsAt: rawEnds } = rule;
    const value = parseNumber(rawValue);
    const startsAt = parseNumber(rawStarts);
    const endsAt = parseNumber(rawEnds);

    switch (eq) {
        case 'in_between':
            if (startsAt !== null && endsAt !== null) {
                covered.push([startsAt, endsAt]);
                maxRef.value = Math.max(maxRef.value, endsAt);
            }
            break;
        case 'equals':
            if (value !== null) {
                covered.push([value, value]);
                maxRef.value = Math.max(maxRef.value, value);
                excluded.delete(value);
            }
            break;
        case 'not_equal_to':
            if (value !== null) excluded.add(value);
            break;
        case 'greater_than':
            if (value !== null) {
                const gtStart = value + 1;
                const gtEnd = value + 10000;
                covered.push([gtStart, gtEnd]);
                maxRef.value = Math.max(maxRef.value, gtEnd);
            }
            break;
        case 'greater_than_or_equals_to':
            if (value !== null) {
                const gteStart = value;
                const gteEnd = value + 10000;
                covered.push([gteStart, gteEnd]);
                maxRef.value = Math.max(maxRef.value, gteEnd);
            }
            break;
        case 'less_than':
            if (value !== null && value > 1) {
                covered.push([1, value - 1]);
                maxRef.value = Math.max(maxRef.value, value - 1);
            }
            break;
        case 'less_than_or_equals_to':
            if (value !== null) {
                covered.push([1, value]);
                maxRef.value = Math.max(maxRef.value, value);
            }
            break;
        default:
            break;
    }
}

export function calculateMissingUnits(rules) {
    const covered = [];
    const excluded = new Set(); 
    const maxRef = { value: 0 }; 

    rules?.forEach(rule => handleRule(rule, covered, excluded, maxRef));

    covered.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const [start, end] of covered) {
        if (merged.length === 0 || merged[merged.length - 1][1] < start - 1) {
            merged.push([start, end]);
        } else {
            merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], end);
        }
    }

    const updatedExcluded = new Set();
    excluded.forEach(val => {
        let isCovered = false;
        merged.forEach(([start, end]) => {
            if (val >= start && val <= end) {
                isCovered = true;
            }
        });
        if (!isCovered) {
            updatedExcluded.add(val);
        }
    });

    const missing = [];
    let current = 1;
    for (const [start, end] of merged) {
        if (current < start) {
            missing.push([current, start - 1]);
        }
        current = end + 1;
    }

    if (current <= maxRef.value) {
        missing.push([current, maxRef.value]);
    }

    updatedExcluded.forEach(val => {
        missing.push([val, val]);
    });

    const result = missing.map(range => `${range[0]}-${range[1]}`).join(", ");
    return result;
}




/**
 * The function `extractFollowUpQuestions` extracts follow-up questions enclosed in `<<` and `>>` or `>
 * >` from a given text and returns the cleaned text without the follow-up questions along with an
 * array of extracted follow-up questions.
 * @param {string} text - The `extractFollowUpQuestions` function takes a string `text` as input and
 * extracts any follow-up questions enclosed within `<<` and `>>` or `> >` delimiters. It then returns
 * an object containing the cleaned text without the follow-up questions and an array of extracted
 * follow-up
 * @returns The `extractFollowUpQuestions` function returns an object with two properties:
 * `cleanedText` and `followUpQuestions`. `cleanedText` contains the original text with follow-up
 * questions removed, and `followUpQuestions` is an array of follow-up questions extracted from the
 * original text enclosed in `<<` and `>>` or `> >` markers.
 */
export const extractFollowUpQuestions = (text: string) => {
    const followUpQuestions: string[] = [];
    const regex = /<<\s*([\s\S]*?)\s*(?:>>|> >)/g;

    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
        followUpQuestions.push(match[1].trim());
    }

    // Remove follow-up questions from the text
    const cleanedText = text.replace(regex, "").trim();

    return { cleanedText, followUpQuestions };
};


/**
 * The `parseStreamChunks` function processes stream chunks, updates messages, and extracts relevant
 * data while skipping certain chunks.
 * @param {string} chunk - The `chunk` parameter in the `parseStreamChunks` function is a string that
 * contains data received in chunks from a stream. This function parses the stream chunks to extract
 * relevant information and update the messages displayed in the UI.
 * @param setMessages - The `setMessages` parameter in the `parseStreamChunks` function is a function
 * that updates the messages state in the component where this function is being used. It is typically
 * a state setter function provided by a React hook like `useState` or `useReducer`. By calling
 * `setMessages` with
 * @returns The `parseStreamChunks` function returns the parsed data accumulated from the stream
 * chunks, or `null` if no data was parsed.
 */
export const stripAiMarkers = (text: string) => {
    if (!text) return text;
    
    // Check if text contains &Analyzing...& followed by actual content
    const analyzingMarker = "&Analyzing...&";
    const infoMarker = "[INFO]";
    
    // If the text is ONLY the marker (or ends with it and has no content after)
    // Replace it with "Analyzing..." to show progress
    if (text === analyzingMarker) {
        return "Analyzing...";
    }
    
    // Check if marker is at the end with no real content after it
    if (text.endsWith(analyzingMarker)) {
        const beforeMarker = text.substring(0, text.length - analyzingMarker.length);
        // If there's nothing before the marker, show "Analyzing..."
        if (!beforeMarker.trim()) {
            return "Analyzing...";
        }
        // If there's content before, remove the marker completely
        return beforeMarker;
    }
    
    // If marker is in the middle (has content after it), remove it completely
    let result = text;
    result = result.split(analyzingMarker).join("");
    result = result.split(infoMarker).join("");
    
    // Handle partial markers at the END of the string (for char-by-char streaming)
    // Check for partial &Analyzing...& at the end
    for (let i = analyzingMarker.length - 1; i >= 1; i--) {
        const prefix = analyzingMarker.substring(0, i);
        if (result.endsWith(prefix)) {
            // Partial marker at the end - hide it for now
            return result.substring(0, result.length - i);
        }
    }
    
    return result;
};

export const parseStreamChunks = (chunk: string, setMessages, onMessageId = null, onSuppRef = null) => {
    const lines = chunk.split("\n");
    let parsedData = "";
    let shouldStop = false;
    let stoppedByUser = false; // Track if user manually stopped

    for (let line of lines) {
        try {
            // Skip empty lines
            if (!line.trim()) continue;
            
            if (!line.startsWith("data: ")) continue;

            const data = line.slice(6).trim();

            // Skip if the entire chunk is just a marker we want to ignore completely
            if (data === "&Analyzing...&" || data === "[INFO]") {
                continue;
            }

            // Skip standard stop markers
            if (["[DONE]", "[STREAM DONE]"].includes(data)) break;

            // Try to parse as JSON
            try {
                const jsonData = JSON.parse(data);
                
                // Check if it's a valid object
                if (typeof jsonData === 'object' && jsonData !== null) {
                    // Handle message_id if present
                    if (jsonData.message_id && onMessageId) {
                        onMessageId(jsonData.message_id);
                    }
                    
                    // ERROR HANDLING
                    if (jsonData.status === "error" && jsonData.error) {
                        return { 
                            parsedData: null, 
                            shouldStop: true, 
                            stoppedByUser: false,
                            hasError: true,
                            errorMessage: jsonData.error
                        };
                    }
                    
                    // Check for stream completion status
                    if (jsonData.status === "STREAM DONE") {
                        shouldStop = true;
                        stoppedByUser = false;
                        break;
                    }
                    
                    // Handle token content
                    if (jsonData.token !== undefined && jsonData.token !== null) {
                        parsedData += String(jsonData.token);
                    }
                    
                    // OLD FORMAT SUPPORT
                    else if ('event' in jsonData) {
                        if (jsonData.event === "message_id" && jsonData.message_id) {
                            if (onMessageId) {
                                onMessageId(jsonData.message_id);
                            }
                            continue;
                        }
                        
                        if (jsonData.event === "stopped") {
                            shouldStop = true;
                            stoppedByUser = true;
                            break;
                        }
                        
                        if (jsonData.event === "token" && jsonData.content !== undefined) {
                            parsedData += String(jsonData.content);
                        }
                    }
                    // Handle supplementary references
                    else if (jsonData.type === "supp_ref" && jsonData.data && onSuppRef) {
                        onSuppRef(jsonData.data);
                    }
                    else if (!jsonData.token && !jsonData.message_id && !jsonData.status) {
                        parsedData += data;
                    }
                } else {
                    parsedData += data;
                }
            } catch (jsonError) {
                parsedData += data;
            }
        } catch (err) {
            console.error("parseStreamChunks error:", err);
        }
    }

    return { 
        parsedData: parsedData || null, 
        shouldStop, 
        stoppedByUser,
        hasError: false,
        errorMessage: null
    };
};






/**
 * The function `parseStreamResponseBasedOnReader` reads and processes a stream of data, updating
 * messages in a chat interface and extracting follow-up questions.
 * @param reader - The `reader` parameter in the `parseStreamResponseBasedOnReader` function is
 * typically a `ReadableStreamDefaultReader` object that allows you to read data from a
 * `ReadableStream`. It provides methods like `read()` to read chunks of data from the stream.
 * @param decoder - The `decoder` parameter in the `parseStreamResponseBasedOnReader` function is used
 * to decode the data read from the stream. It is responsible for converting the raw data into a
 * human-readable format. In this function, the `decoder.decode()` method is used to decode the value
 * read from the
 * @param fullAssistantText - The `fullAssistantText` parameter in the
 * `parseStreamResponseBasedOnReader` function is a string that represents the complete text generated
 * by the assistant during the streaming process. It is updated as new chunks of text are received and
 * processed from the stream.
 * @param setMessages - The `setMessages` parameter is a function that allows you to update the
 * messages displayed in the UI. It is typically used to add new messages, update existing messages, or
 * remove messages from the chat interface based on the data received from the stream response.
 * @param sessionId - The `sessionId` parameter is used to identify a specific session or conversation.
 * It can be a unique identifier assigned to each session to keep track of the conversation context and
 * history. This parameter helps in managing multiple concurrent sessions and ensuring that the
 * responses are associated with the correct session.
 * @param [onStreamStart=null] - The `onStreamStart` parameter is a function that is called when the
 * stream processing starts. It takes `sessionId` as an argument, which can be used to identify the
 * session for which the stream processing is being initiated. This function is optional and can be
 * provided to perform any necessary actions or updates
 * @param [onStreamEnd=null] - The `onStreamEnd` parameter is a function that is called when the stream
 * processing ends. It can be used to perform any cleanup or finalization tasks related to the stream
 * processing.
 * @returns The function `parseStreamResponseBasedOnReader` returns the follow-up questions extracted
 * from the full assistant text after processing the stream response.
 */
export const parseStreamResponseBasedOnReader = async (
    reader,
    decoder,
    fullAssistantText,
    setMessages,
    sessionId,
    onStreamStart=null,
    onStreamEnd=null,
    onMessageId=null,
    isBlocked=null,
    onSuppRef=null
) => {
    if (onStreamStart) onStreamStart(sessionId);
    let wasStopped = false;
    let capturedMessageId = null; // Capture the message ID from stream

    // Create a callback to capture the message ID (only once)
    const captureMessageId = (messageId) => {
        // Only capture the first time we see a message ID
        if (!capturedMessageId) {
            capturedMessageId = messageId;
            if (onMessageId) {
                onMessageId(messageId);
            }
        }
    };

    try {
        while (true) {
            // Check if blocked before each read
            if (isBlocked && isBlocked()) break;

            const { done, value } = await reader.read();
            if (done) break;

            const rawChunk = decoder.decode(value, { stream: true });
            const { parsedData: rawText, shouldStop, stoppedByUser: wasStoppedByUser, hasError, errorMessage } = parseStreamChunks(rawChunk, setMessages, captureMessageId, onSuppRef);

            // ERROR HANDLING: If stream error detected, show error message and stop
            if (hasError && errorMessage) {
                setMessages(prev => {
                    const updated = [...prev];
                    const idx = updated.findIndex(
                        m => (m?.loading || m?.stream) && m.createdBy === "assistant"
                    );
                    if (idx !== -1) {
                        updated[idx] = {
                            ...updated[idx],
                            body: `Error: ${errorMessage}`,
                            createdBy: "assistant",
                            loading: false,
                            stream: undefined,
                            loadingSpin: false,
                            failed: true,
                            status: 'failed',
                            createdAt: new Date()
                        };
                    }
                    return updated;
                });
                // Return early with error
                return { followUpQuestions: [], messageId: capturedMessageId, hasError: true, errorMessage };
            }

            // Update message even if rawText is empty (to clear loading states)
            // This ensures loading indicator is removed as soon as stream starts
            // IMPORTANT: Do this BEFORE checking shouldStop so message is updated even if stream ends in one chunk
            fullAssistantText += rawText || '';
            const displayBody = stripAiMarkers(fullAssistantText);

            // Double check block status before UI update
            if (isBlocked && isBlocked()) break;

            setMessages(prev => {
                const updated = [...prev];
                // Last message from assistant
                const idx = updated.findIndex(
                    m => (m?.loading || m?.stream) && m.createdBy === "assistant"
                );
                if (idx !== -1) {
                    updated[idx] = {
                        ...updated[idx],
                        body: displayBody || "...",
                        createdBy: "assistant",
                        loading: false,
                        stream: true,
                        loadingSpin: false,
                        createdAt: new Date(),
                        // Add the message ID if we have it
                        ...(capturedMessageId ? { id: capturedMessageId } : {})
                    };
                }
                return updated;
            });

            // Check if stopped AFTER updating the message
            if (shouldStop) {
                wasStopped = wasStoppedByUser; // Only set if user manually stopped
                break;
            }
        }

        // Final block check before finalizing the message
        if (isBlocked && isBlocked()) {
            return { followUpQuestions: [], messageId: capturedMessageId };
        }

        // Extract follow-up AFTER stream ends (correct)
        const { cleanedText, followUpQuestions } =
            extractFollowUpQuestions(stripAiMarkers(fullAssistantText));

        // Fix the UI text now and mark as stopped if needed:
        setMessages(prev => {
            const updated = [...prev];
            const idx = updated.findIndex(m => m.stream);
            if (idx !== -1) {
                updated[idx] = {
                    ...updated[idx],
                    body: cleanedText,
                    loading: false,
                    loadingSpin: false,
                    stream: undefined,
                    status: wasStopped ? 'stopped' : 'complete', // Mark as complete or stopped
                    ...(wasStopped ? { stoppedByUser: true } : {}),
                    // Ensure message ID is set
                    ...(capturedMessageId && !updated[idx].id ? { id: capturedMessageId } : {})
                };
            }
            return updated;
        });

        // Return both follow-up questions and the message ID
        return { followUpQuestions, messageId: capturedMessageId };

    } finally {
        if (onStreamEnd) onStreamEnd(sessionId);
    }
};


export const sortMonthNamesChronologically = (data) => {
    const entries = Object.entries(data);

    const sortedEntries = entries.sort(([a], [b]) => {
        const dateA: any = new Date(a);
        const dateB: any = new Date(b);
        return dateA - dateB;
    });

    return Object.fromEntries(sortedEntries);
}

export const getRidOfTrailingSpaces = (value) =>
    value
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v)
        .join(",")
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v)
        .join(",");
  
